import React from 'react';
import { Link } from 'react-router-dom';
import BackButton from '../components/BackButton';
import { Trash2, ShieldAlert, CheckCircle2, HelpCircle, Mail, Phone, Lock, Smartphone, Monitor, Building2, UserCheck } from 'lucide-react';

export default function AccountDeletion() {
  return (
    <div style={{ minHeight: 'calc(100vh - 72px)', background: '#F8FAF7', fontFamily: "'DM Sans', sans-serif", color: '#161616', paddingBottom: '4rem' }}>
      
      {/* Hero Header */}
      <div style={{ background: 'linear-gradient(135deg, #084734 0%, #032b1f 100%)', padding: '3.5rem 1.5rem 4rem', color: 'white', position: 'relative', overflow: 'hidden' }}>
        <div style={{ maxWidth: 900, margin: '0 auto', position: 'relative', zIndex: 2 }}>
          <div style={{ marginBottom: '1.5rem' }}>
            <BackButton />
          </div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(206, 241, 123, 0.15)', border: '1px solid rgba(206, 241, 123, 0.3)', color: '#CEF17B', padding: '6px 16px', borderRadius: '50px', fontSize: '0.82rem', fontWeight: 800, marginBottom: '1.2rem' }}>
            <Trash2 size={16} /> Account Privacy & Management
          </div>
          <h1 style={{ fontSize: '2.5rem', fontWeight: 900, margin: '0 0 1rem 0', letterSpacing: '-0.5px', lineHeight: 1.2 }}>
            Account Deletion & Data Request Guide
          </h1>
          <p style={{ fontSize: '1.05rem', color: '#DCEFB8', maxWidth: 720, margin: 0, fontWeight: 500, lineHeight: 1.6 }}>
            At TurfX, we respect your right to control your personal information. This guide explains step-by-step how both <strong>Customer Players</strong> and <strong>Venue Partners</strong> can delete their account and data directly from our Mobile App or Website.
          </p>
        </div>
      </div>

      <div style={{ maxWidth: 900, margin: '-2rem auto 0', padding: '0 1.5rem', position: 'relative', zIndex: 3 }}>
        
        {/* Section 1: Customer Account Deletion Steps */}
        <div style={{ background: 'white', borderRadius: '24px', border: '1.5px solid #EEF2E6', padding: '2.5rem', boxShadow: '0 10px 30px rgba(0,0,0,0.04)', marginBottom: '2rem' }}>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '1.5rem' }}>
            <div style={{ width: 44, height: 44, borderRadius: '12px', background: '#E0E7FF', color: '#3730A3', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <UserCheck size={24} />
            </div>
            <div>
              <h2 style={{ fontSize: '1.4rem', fontWeight: 900, color: '#161616', margin: 0 }}>
                1. Customer (Player) Account Deletion
              </h2>
              <div style={{ fontSize: '0.85rem', color: '#6B7280', fontWeight: 500 }}>For sports players & users booking turfs</div>
            </div>
          </div>

          <p style={{ color: '#374151', fontSize: '0.95rem', lineHeight: 1.6, marginBottom: '2rem' }}>
            Customers can request and execute account deletion directly inside the TurfX Mobile App or Web site:
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
            
            {/* Customer Mobile App Steps */}
            <div style={{ background: '#F8FAF7', borderRadius: '18px', border: '1.5px solid #EEF2E6', padding: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '1rem' }}>
                <Smartphone size={20} color="#084734" />
                <h3 style={{ fontSize: '1.05rem', fontWeight: 800, color: '#161616', margin: 0 }}>Via TurfX Mobile App</h3>
              </div>
              <ol style={{ paddingLeft: '1.2rem', margin: 0, color: '#374151', fontSize: '0.88rem', lineHeight: 1.8, fontWeight: 600 }}>
                <li>Open the <strong>TurfX Mobile App</strong> on your device.</li>
                <li>Tap the <strong>Profile</strong> tab in the bottom bar.</li>
                <li>Scroll down to the <strong>Danger Zone</strong> section.</li>
                <li>Tap the red <strong>Delete Account</strong> button.</li>
                <li>Confirm the alert pop-up to finalize deletion.</li>
              </ol>
            </div>

            {/* Customer Web App Steps */}
            <div style={{ background: '#F8FAF7', borderRadius: '18px', border: '1.5px solid #EEF2E6', padding: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '1rem' }}>
                <Monitor size={20} color="#084734" />
                <h3 style={{ fontSize: '1.05rem', fontWeight: 800, color: '#161616', margin: 0 }}>Via TurfX Website</h3>
              </div>
              <ol style={{ paddingLeft: '1.2rem', margin: 0, color: '#374151', fontSize: '0.88rem', lineHeight: 1.8, fontWeight: 600 }}>
                <li>Log in to your account at <strong>turfx.in</strong>.</li>
                <li>Click <strong>My Profile</strong> from the top right navigation.</li>
                <li>Scroll down to the <strong>Danger Zone</strong> card.</li>
                <li>Click the <strong>Delete Account</strong> button.</li>
                <li>Confirm the pop-up warning dialog.</li>
              </ol>
            </div>

          </div>

          <div style={{ background: '#F0FDF4', borderRadius: '16px', border: '1px solid #86EFAC', padding: '1rem 1.25rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
            <span style={{ fontSize: '0.88rem', fontWeight: 700, color: '#166534' }}>Logged in as a Customer? Manage your profile here:</span>
            <Link to="/profile" style={{ background: '#084734', color: '#CEF17B', padding: '8px 18px', borderRadius: '10px', textDecoration: 'none', fontWeight: 800, fontSize: '0.85rem' }}>
              Customer Profile →
            </Link>
          </div>

        </div>

        {/* Section 2: Partner Account Deletion Steps */}
        <div style={{ background: 'white', borderRadius: '24px', border: '1.5px solid #EEF2E6', padding: '2.5rem', boxShadow: '0 10px 30px rgba(0,0,0,0.04)', marginBottom: '2rem' }}>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '1.5rem' }}>
            <div style={{ width: 44, height: 44, borderRadius: '12px', background: '#DCEFB8', color: '#084734', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Building2 size={24} />
            </div>
            <div>
              <h2 style={{ fontSize: '1.4rem', fontWeight: 900, color: '#161616', margin: 0 }}>
                2. Venue Partner (Owner) Account Deletion
              </h2>
              <div style={{ fontSize: '0.85rem', color: '#6B7280', fontWeight: 500 }}>For turf owners & sports venue managers</div>
            </div>
          </div>

          <p style={{ color: '#374151', fontSize: '0.95rem', lineHeight: 1.6, marginBottom: '2rem' }}>
            Venue partners can delete their partner account directly inside the Partner Portal or Partner Mobile View:
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
            
            {/* Partner Mobile Steps */}
            <div style={{ background: '#F8FAF7', borderRadius: '18px', border: '1.5px solid #EEF2E6', padding: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '1rem' }}>
                <Smartphone size={20} color="#084734" />
                <h3 style={{ fontSize: '1.05rem', fontWeight: 800, color: '#161616', margin: 0 }}>Via Partner Mobile App</h3>
              </div>
              <ol style={{ paddingLeft: '1.2rem', margin: 0, color: '#374151', fontSize: '0.88rem', lineHeight: 1.8, fontWeight: 600 }}>
                <li>Open <strong>Partner Dashboard</strong> in the app.</li>
                <li>Tap the <strong>Profile</strong> tab in the menu.</li>
                <li>Scroll to the <strong>Danger Zone</strong> section.</li>
                <li>Tap <strong>Delete Partner Account</strong>.</li>
                <li>Confirm the pop-up prompt to deactivate.</li>
              </ol>
            </div>

            {/* Partner Web Portal Steps */}
            <div style={{ background: '#F8FAF7', borderRadius: '18px', border: '1.5px solid #EEF2E6', padding: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '1rem' }}>
                <Monitor size={20} color="#084734" />
                <h3 style={{ fontSize: '1.05rem', fontWeight: 800, color: '#161616', margin: 0 }}>Via Partner Web Portal</h3>
              </div>
              <ol style={{ paddingLeft: '1.2rem', margin: 0, color: '#374151', fontSize: '0.88rem', lineHeight: 1.8, fontWeight: 600 }}>
                <li>Log in to <strong>turfx.in/partner</strong>.</li>
                <li>Click <strong>Profile</strong> under Account in the sidebar.</li>
                <li>Scroll down to the red <strong>Danger Zone</strong> card.</li>
                <li>Click <strong>Delete Partner Account</strong>.</li>
                <li>Confirm deletion to unpublish listings and log out.</li>
              </ol>
            </div>

          </div>

          <div style={{ background: '#FEF2F2', borderRadius: '16px', border: '1px solid #FCA5A5', padding: '1rem 1.25rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
            <span style={{ fontSize: '0.88rem', fontWeight: 700, color: '#991B1B' }}>Logged in as a Venue Partner? Manage partner settings:</span>
            <Link to="/partner" style={{ background: '#DC2626', color: 'white', padding: '8px 18px', borderRadius: '10px', textDecoration: 'none', fontWeight: 800, fontSize: '0.85rem' }}>
              Partner Portal →
            </Link>
          </div>

        </div>

        {/* Section 3: Data Retention & Privacy Policy */}
        <div style={{ background: 'white', borderRadius: '24px', border: '1.5px solid #EEF2E6', padding: '2.5rem', boxShadow: '0 10px 30px rgba(0,0,0,0.04)', marginBottom: '2rem' }}>
          <h2 style={{ fontSize: '1.3rem', fontWeight: 900, color: '#161616', marginTop: 0, marginBottom: '1.2rem', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Lock size={22} color="#084734" /> What Happens to Your Data Upon Account Deletion?
          </h2>
          
          <ul style={{ paddingLeft: '1.2rem', margin: 0, color: '#374151', fontSize: '0.92rem', lineHeight: 1.9, fontWeight: 500 }}>
            <li><strong>Immediate Session Termination:</strong> Your active session is immediately invalidated and you will be logged out on all devices.</li>
            <li><strong>Removal from Active Use:</strong> Your profile info, login credentials, and stored preferences are deleted from active platform services.</li>
            <li><strong>Notifications Stopped:</strong> You will no longer receive promotional emails, SMS notifications, or booking alerts.</li>
            <li><strong>Legal & Financial Compliance:</strong> In accordance with Indian tax laws (GST laws) and RBI banking regulations, past transaction records, tax invoices, and completed booking receipts are retained for mandatory audit retention periods.</li>
          </ul>
        </div>

        {/* Section 4: Contact Support for Manual Requests */}
        <div style={{ background: 'white', borderRadius: '24px', border: '1.5px solid #EEF2E6', padding: '2.5rem', boxShadow: '0 10px 30px rgba(0,0,0,0.04)' }}>
          <h2 style={{ fontSize: '1.3rem', fontWeight: 900, color: '#161616', marginTop: 0, marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <HelpCircle size={22} color="#084734" /> Need Assistance with Manual Data Removal?
          </h2>
          <p style={{ color: '#374151', fontSize: '0.92rem', lineHeight: 1.6, marginBottom: '1.5rem' }}>
            If you face any difficulty accessing your TurfX account or need manual assistance with your data deletion request, reach out to our privacy support team.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
            <a href="mailto:support@metaqode.co.in?subject=Account%20Deletion%20Request" style={{ textDecoration: 'none', background: '#F8FAF7', border: '1.5px solid #EEF2E6', borderRadius: '14px', padding: '1.2rem', display: 'flex', alignItems: 'center', gap: '12px', color: '#161616', transition: 'all 0.2s' }}>
              <Mail size={22} color="#084734" />
              <div>
                <div style={{ fontSize: '0.75rem', color: '#374151', fontWeight: 700, textTransform: 'uppercase' }}>Email Privacy Team</div>
                <div style={{ fontWeight: 800, fontSize: '0.9rem', color: '#084734' }}>support@metaqode.co.in</div>
              </div>
            </a>

            <Link to="/contact" style={{ textDecoration: 'none', background: '#F8FAF7', border: '1.5px solid #EEF2E6', borderRadius: '14px', padding: '1.2rem', display: 'flex', alignItems: 'center', gap: '12px', color: '#161616', transition: 'all 0.2s' }}>
              <Phone size={22} color="#084734" />
              <div>
                <div style={{ fontSize: '0.75rem', color: '#374151', fontWeight: 700, textTransform: 'uppercase' }}>Support Helpdesk</div>
                <div style={{ fontWeight: 800, fontSize: '0.9rem', color: '#084734' }}>Submit Support Ticket</div>
              </div>
            </Link>
          </div>
        </div>

      </div>

    </div>
  );
}
